import { each, isEmpty } from 'lodash-es'
import { api } from '@/api'
import { findInTree, createChildren } from '@/modules/utils'
import to from '@/modules/await-to-js'
import saveAs from 'file-saver'

const state = {
  treeNodes: {
    name: '/',
    path: '/',
    directory: true,
    fetched: false,
    children: []
  },
  currentDirectory: {},
  currentDirectoryContent: [],
  selectedElement: null,
  clipboard: null
}

export const getters = {
  treeNodes: state => state.treeNodes,
  currentDirectory: state => state.currentDirectory,
  currentDirectoryContent: state => state.currentDirectoryContent,
  selectedElement: state => state.selectedElement,
  clipboard: state => state.clipboard
}

export const mutations = {
  // Set global tree nodes
  setTreeNodes (state, treeNodes) {
    state.treeNodes = treeNodes
  },

  // Insert children into a node
  insertChildrenInNode (state, options) {
    // Create path of every children
    each(options.children, child => { child.path = `${options.parentNode.path}${child.name}/` })
    // Attach children
    options.parentNode.children = options.children
    // Set attribute to know that parent has been fetched
    options.parentNode.fetched = true
  },

  // Open or close tree node
  toggleTreeNode (state, treeNode) {
    treeNode.state.opened = !treeNode.state.opened
  },

  // Set current selected directory (tree)
  setCurrentDirectory (state, currentDirectory) {
    state.currentDirectory = currentDirectory
  },

  // Set current selected directory content (tree)
  setCurrentDirectoryContent (state, currentDirectoryContent) {
    state.currentDirectoryContent = currentDirectoryContent
  },

  // Set current selected item (explorer)
  setSelectedElement (state, selectedElement) {
    state.selectedElement = selectedElement
  },

  // Set current selected item to clipboard (mode: copy/cut)
  setClipboard (state, options = {}) {
    state.clipboard = {
      element: options.element || state.selectedElement,
      mode: options.mode
    }
  },

  // Reset fetched status
  resetFetchedStatus (state, nodePaths) {
    // Set original directory to unfetched
    each(nodePaths, path => {
      let treeNode = findInTree(state.treeNodes.children, path)
      // Root
      if (!treeNode) treeNode = state.treeNodes
      treeNode.fetched = false
    })
  },

  // Reset clipboard
  resetClipboard (state) {
    // Reset clipboard
    state.clipboard = null
  },

  // Reset this store when leaving file explorer
  resetStore (state) {
    state.treeNodes = {
      name: '/',
      path: '/',
      directory: true,
      fetched: false,
      children: []
    }
    state.currentDirectory = {}
    state.currentDirectoryContent = []
    state.selectedElement = null
    state.clipboard = null
  }
}

export const actions = {
  // Get directory
  async getDirectory (context, options = {}) {
    // If already fetched, take the existing data
    if (!options.forceRefresh && (options.parentNode && options.parentNode.fetched)) {
      if (options.changeCurrentDirectory) {
        // context.commit('setSelectedElement', options.parentNode)
        context.commit('setCurrentDirectory', options.directory)
        context.commit('setCurrentDirectoryContent', options.parentNode.children)
      }
      return
    }

    let error, response
    [error, response] = await to(api.getDirectory(context.rootGetters.gateway, options.directory))
    if (error || !response) return

    // If no parent node provided, get the root node
    if (!options.parentNode) {
      options.parentNode = state.treeNodes
    }

    const children = createChildren(response.data.list, options.parentNode)
    // Save tree in store
    context.commit('insertChildrenInNode', { parentNode: options.parentNode, children })

    // Change selected element on click
    if (options.changeCurrentDirectory) {
      // context.commit('setSelectedElement', options.parentNode)
      context.commit('setCurrentDirectory', options.directory)
      context.commit('setCurrentDirectoryContent', children)
    }
  },

  // Get file
  async getFile (context, options = {}) {
    // Do nothing if selected element is a directory
    if (context.state.selectedElement.directory) throw new Error('Directories cannot be downloaded')

    // Remove last /, needed for renaming files
    const path = context.state.selectedElement.path.slice(0, context.state.selectedElement.path.length - 1)

    let error, response
    [error, response] = await to(api.getFile(context.rootGetters.gateway, path))
    if (error || !response) return

    let file = null
    try {
      file = new File([response.data], context.state.selectedElement.name)
    } catch (e) {
      file = new Blob([response.data])
    }
    saveAs(file, context.state.selectedElement.name)
  },

  // Upload a file to current directory
  async uploadFile (context, options = {}) {
    const formData = new FormData()
    formData.append(options.fieldName, options.file, options.file.name)
    await api.uploadFile(context.rootGetters.gateway, formData, options.file.name, context.state.currentDirectory)
  },

  // Create a new folder
  async createNewFolder (context, directoryName) {
    await api.createNewFolder(context.rootGetters.gateway, context.state.currentDirectory + directoryName)
  },

  // Edit name
  async edit (context, name) {
    // Remove last /, needed for renaming files
    const path = context.state.selectedElement.path.slice(0, context.state.selectedElement.path.length - 1)
    await api.move(context.rootGetters.gateway, path, context.state.currentDirectory + name)
  },

  // Paste clipboard
  async paste (context, options = {}) {
    // Remove last /, needed for renaming files
    const source = context.state.clipboard.element.path.slice(0, context.state.clipboard.element.path.length - 1)

    const destination = !isEmpty(options.directory)
      ? options.directory.path + context.state.clipboard.element.name
      : context.state.currentDirectory + context.state.clipboard.element.name

    if (context.state.clipboard.mode === 'copy') {
      // Copy
      let error
      [error] = await to(api.paste(context.rootGetters.gateway, source, destination))
      if (error) return

      context.commit('resetFetchedStatus', [!isEmpty(options.directory) ? options.directory.path : context.state.currentDirectory])
    } else {
      // Move (cut)
      let error
      [error] = await to(api.move(context.rootGetters.gateway, source, destination))
      if (error) return

      // Reset fetched status of source and destination
      context.commit('resetFetchedStatus', [
        context.state.clipboard.element.parentNode.path,
        !isEmpty(options.directory) ? options.directory.path : context.state.currentDirectory
      ])
      // Reset clipboard when moving
      context.commit('resetClipboard')
    }
  },

  // Remove file / directory
  async remove (context, options = {}) {
    const file = !isEmpty(options.element)
      ? options.element.path
      : context.state.selectedElement.path

    await to(api.remove(context.rootGetters.gateway, file))
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
